/**
 * src/components/ui/Toast.jsx — transient status messages ("Deposit paid",
 * "Consent form sent"), per REDESIGN.md's copy rule: a button says what
 * will happen, the toast that follows echoes what did happen.
 *
 * Stacked in one `popover="manual"` container (modern-web-guidance's
 * `persistent-toast-notifications` guide) so every toast promotes to the
 * top layer — above any open Dialog/Drawer, never clipped by a parent's
 * `overflow`. `manual` (not `auto`) specifically because a toast must
 * survive the user clicking elsewhere on the page; `auto` popovers
 * light-dismiss on outside interaction, which would make a toast disappear
 * the moment someone kept working.
 *
 * Popover is Baseline "Newly available" (since 2025-01-27) — per this
 * project's browser support policy that needs feature detection and
 * graceful degradation, not an unconditional assumption. Feature-detected
 * via `'popover' in HTMLElement.prototype`: when unsupported, the same
 * container renders as a plain `position: fixed` stack instead (ui.css's
 * `.ui-toast-region` has a static fallback position; the popover-supported
 * path additionally gets top-layer promotion). Functionally identical
 * either way — visible, dismissible, auto-timed — only the top-layer
 * stacking guarantee is progressive enhancement.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const ToastContext = createContext(null);

let nextId = 0;

/**
 * @returns {{ push: (message: string, opts?: { variant?: 'default'|'success'|'error', duration?: number }) => void }}
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast() must be used inside <ToastProvider>');
  return ctx;
}

/**
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const regionRef = useRef(null);
  const supportsPopover = typeof HTMLElement !== 'undefined' && 'popover' in HTMLElement.prototype;

  const remove = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message, opts = {}) => {
      const id = nextId++;
      const duration = opts.duration ?? 4000;
      setToasts((current) => [...current, { id, message, variant: opts.variant ?? 'default' }]);
      if (duration > 0) {
        setTimeout(() => remove(id), duration);
      }
    },
    [remove],
  );

  // Popover content only renders in the top layer while "showing" — open it
  // the moment there's something to show, close it once the stack is empty
  // again, rather than leaving an always-open-but-empty popover.
  useEffect(() => {
    if (!supportsPopover || !regionRef.current) return;
    if (toasts.length > 0) {
      if (!regionRef.current.matches(':popover-open')) regionRef.current.showPopover();
    } else if (regionRef.current.matches(':popover-open')) {
      regionRef.current.hidePopover();
    }
  }, [toasts.length, supportsPopover]);

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <div
        ref={regionRef}
        className="ui-toast-region"
        popover={supportsPopover ? 'manual' : undefined}
        role="region"
        aria-label="Notifications"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`ui-toast ui-toast--${t.variant}`}
            // Errors are assertive (interrupt immediately); everything else
            // is polite status — a run of "Deposit paid" toasts shouldn't
            // cut off whatever a screen reader is already reading.
            role={t.variant === 'error' ? 'alert' : 'status'}
          >
            <p>{t.message}</p>
            <button
              type="button"
              className="ui-toast-close"
              aria-label="Dismiss notification"
              onClick={() => remove(t.id)}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
